import { supabase } from "@/integrations/supabase/client";
import { saveEventSimulation } from "@/components/event-simulator/utils/apiService";
import { loadCacheData, updateCacheData } from "@/utils/sessionStorage";
import { optimizeSelectedSuppliers } from "./supplierJsonOptimizer";
import { formatDateForStorage } from "./dateUtils";

/**
 * Formats monetary values to exactly 2 decimal places
 */
export const formatMonetaryValue = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const numValue = typeof value === 'number' ? value : parseFloat(String(value));
  return isNaN(numValue) ? 0 : parseFloat(numValue.toFixed(2));
};

interface PersistParams {
  searchData: {
    selectedCategories: string[];
    guests: string;
    budget: string;
    date: Date;
    cep: string;
  } | null;
  selectedSuppliers: { [key: string]: any[] };
  selectedDisposableProducts: any[];
  selectedDecorationItems: any[];
  selectedSweetsItems: any[];
  selectedCakeItems: any[];
  userId?: string;
}

const calculateTotal = (
  selectedSuppliers: { [key: string]: any[] },
  selectedDisposableProducts: any[],
  selectedDecorationItems: any[],
  selectedSweetsItems: any[],
  selectedCakeItems: any[]
) => {
  let total = 0;
  try {
    Object.values(selectedSuppliers).forEach((suppliers) => {
      suppliers.forEach((supplier: any) => {
        const supplierTotal = formatMonetaryValue(supplier.totalPrice);
        total += supplierTotal;
      });
    });

    selectedDisposableProducts.forEach((variant: any) => {
      const variantTotal = formatMonetaryValue(variant.totalPrice);
      total += variantTotal;
    });

    selectedDecorationItems.forEach((item: any) => {
      const decorationTotal = formatMonetaryValue(item.totalPrice || item.price);
      total += decorationTotal;
    });

    selectedSweetsItems.forEach((item: any) => {
      const sweetTotal = formatMonetaryValue(item.totalPrice || item.price);
      total += sweetTotal;
    });

    selectedCakeItems.forEach((item: any) => {
      const cakeTotal = formatMonetaryValue(item.totalPrice || item.price);
      total += cakeTotal;
    });
  } catch (e) {
    console.error("Error calculating total (persist):", e);
  }
  return formatMonetaryValue(total);
};

// Helper function to find existing simulation by session
const findExistingSimulationBySession = async (sessionId: string, userId?: string) => {
  try {
    let query = (supabase as any)
      .from("events")
      .select("id")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1);

    if (userId) {
      // For authenticated users, find by user_id
      query = query.eq("user_id", userId);
    } else {
      // For anonymous users, find by session_id
      query = query
        .is("user_id", null)
        .filter("session_info->>session_id", "eq", sessionId);
    }

    const { data, error } = await query.maybeSingle();
    
    if (error) {
      console.error("Error finding existing simulation:", error);
      return null;
    }

    return data?.id || null;
  } catch (error) {
    console.error("Error finding existing simulation:", error);
    return null;
  }
};

export async function persistSelectionToDatabase(params: PersistParams) {
  const {
    searchData,
    selectedSuppliers,
    selectedDisposableProducts,
    selectedDecorationItems,
    selectedSweetsItems,
    selectedCakeItems,
    userId,
  } = params;

  try {
    if (!searchData) return;

    // Build selectedSuppliersData shape like finalization
    const selectedSuppliersData: { [key: string]: any } = {};

    Object.entries(selectedSuppliers).forEach(([categoryId, suppliers]) => {
      selectedSuppliersData[categoryId] = suppliers.map((supplier: any) => ({
        id: supplier.id,
        name: supplier.name,
        category: supplier.category,
        totalPrice: formatMonetaryValue(supplier.totalPrice),
        basePrice: formatMonetaryValue(supplier.basePrice),
        description: supplier.description,
        image: supplier.image || supplier.imageUrl,
        variants: supplier.variants || [],
        selectedVariant: supplier.selectedVariant || null,
        quantity: supplier.quantity || 1,
        additionalItems: supplier.additionalItems || [],
        necessaryItems: supplier.necessaryItems || [],
        priceAdditionalHour: formatMonetaryValue(supplier.priceAdditionalHour),
        specialPricing: supplier.specialPricing || false,
        specialPrice: formatMonetaryValue(supplier.specialPrice),
        selectedOptionalItems: supplier.selectedOptionalItems || [],
      }));
    });

    // Processar descartáveis
    const disposableItems = selectedDisposableProducts.length > 0 
      ? selectedDisposableProducts.map((variant: any) => ({
          id: variant.id,
          product_id: variant.product_id,
          product_name: variant.product_name,
          sub_product: variant.sub_product,
          seller: variant.seller,
          supplier_id: variant.supplier_id || variant.suppliers?.id,
          product_code: variant.product_code,
          material: variant.material,
          color: variant.color,
          size: variant.size,
          units: variant.units,
          price: formatMonetaryValue(variant.price),
          quantity: variant.quantity,
          totalPrice: formatMonetaryValue(variant.totalPrice),
          category: "descartaveis",
          image_url: variant.image_url || variant.product?.image_url,
        }))
      : [];

    // Processar decoração
    const decorationItems = selectedDecorationItems.length > 0 
      ? selectedDecorationItems.map((item: any, index: number) => ({
          id: item.id || `decoration-${index}`,
          name: item.name || "Item de Decoração",
          category: "decoracao",
          totalPrice: formatMonetaryValue(item.price || item.totalPrice),
          price: formatMonetaryValue(item.price),
          ...item,
        }))
      : [];

    // Verificar se decoração e descartáveis têm o mesmo supplier_id para unificar
    const shouldUnifyDecoracaoDescartaveis = disposableItems.length > 0 && 
      decorationItems.length > 0 && 
      disposableItems[0]?.supplier_id && 
      decorationItems[0]?.supplier_id &&
      disposableItems[0].supplier_id === decorationItems[0].supplier_id;

    if (shouldUnifyDecoracaoDescartaveis) {
      // Unificar decoração e descartáveis em uma única categoria
      selectedSuppliersData["decoracao_descartaveis"] = [...decorationItems, ...disposableItems];

    } else {
      // Manter separados se forem fornecedores diferentes ou só um tipo existir
      if (disposableItems.length > 0) {
        selectedSuppliersData["descartaveis"] = disposableItems;
      }
      if (decorationItems.length > 0) {
        selectedSuppliersData["decoracao"] = decorationItems;
      }
    }

    if (selectedSweetsItems.length > 0) {
      selectedSuppliersData["doces"] = selectedSweetsItems.map((item: any, index: number) => ({
        id: item.id || `sweets-${index}`,
        name: item.name || "Doce",
        category: "doces",
        price: formatMonetaryValue(item.price),
        quantity: item.quantity || 1,
        totalPrice: formatMonetaryValue(item.totalPrice || item.price),
        ...item,
      }));
    }

    if (selectedCakeItems.length > 0) {
      if (!selectedSuppliersData["doces"]) {
        selectedSuppliersData["doces"] = [];
      }
      const cakeItems = selectedCakeItems.map((item: any, index: number) => ({
        id: item.id || `cake-${index}`,
        name: item.name || "Bolo",
        category: "doces",
        price: formatMonetaryValue(item.price),
        totalPrice: formatMonetaryValue(item.totalPrice || item.price),
        size: item.size,
        flavor: item.flavor,
        filling: item.filling,
        filling2: item.filling2,
        decoration: item.decoration,
        type: "bolo",
        ...item,
      }));
      selectedSuppliersData["doces"] = [
        ...selectedSuppliersData["doces"],
        ...cakeItems,
      ];
    }

    const total = calculateTotal(
      selectedSuppliers,
      selectedDisposableProducts,
      selectedDecorationItems,
      selectedSweetsItems,
      selectedCakeItems
    );

    // Apply optimization to the selected suppliers data
    const optimizedData = optimizeSelectedSuppliers(selectedSuppliersData, parseInt(searchData.guests));


    const eventData: any = {
      eventType: "personalizado",
      guests: searchData.guests,
      location: { city: "São Paulo", state: "SP" },
      cep: searchData.cep,
      selectedCategories: searchData.selectedCategories,
      budget: searchData.budget,
      date: formatDateForStorage(searchData.date),
      selectedSuppliers: optimizedData,
      calculatedTotal: formatMonetaryValue(total),
      preferences: {
        userRole: "customer",
        additionalDetails: `Auto-save de seleção parcial nas categorias: ${searchData.selectedCategories.join(
          ", "
        )}`,
      },
    };

    const cache = loadCacheData();
    let existingId = cache?.simulationId as string | undefined;

    // If no cached ID, try to find existing simulation by session
    if (!existingId) {
      const sessionId = cache?.sessionId || null;
      if (sessionId) {
        existingId = await findExistingSimulationBySession(sessionId, userId);
        if (existingId) {
          updateCacheData("simulationId", existingId as any);
        }
      }
    }

    if (!existingId) {
      // Create new draft simulation only if no existing one found
      const newId = await saveEventSimulation(eventData, userId);
      if (newId) {
        updateCacheData("simulationId", newId as any);
      }
      return;
    }

    // Update existing simulation
    if (userId) {
      const { error } = await (supabase as any)
        .from("events")
        .update({
          menu: optimizedData,
          calculated_total: formatMonetaryValue(total),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingId);

      if (error) {
        console.warn("Skip update due to RLS or error:", error.message);
      } else {
      }
    } else {
      // For anonymous users, try to update without user restriction
      const { error } = await (supabase as any)
        .from("events")
        .update({
          menu: optimizedData,
          calculated_total: formatMonetaryValue(total),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingId)
        .is("user_id", null);

      if (error) {
        console.warn("Skip anonymous update due to error:", error.message);
      } else {
      }
    }
  } catch (error) {
    console.error("Error persisting selection:", error);
  }
}
